import type { ContentHash, ContentRef } from "@cogo/publisher-core";

// Browser-safe hashing. `@cogo/publisher-core`'s `hashContent` is `node:crypto`
// over its canonical JSON; `hashCanonicalContent` is the byte-identical pure-JS
// implementation (same canonicalizer, same SHA-256 of the same UTF-8 string).
// A VALUE import of `@cogo/publisher-core` pulls its index barrel — and
// `node:crypto` — into any browser bundle that reaches this module, which the
// Studio artifact-observation seam now does.
import { hashCanonicalContent } from "./canonical-content-hash";

import type { AcceptanceInventoryKind } from "./application-acceptance-manifest";

export interface ApplicationProfileBody {
  profileId: string;
  profileVersion: string;
  applicationId: string;
  displayName: string;
  applicationOntologyPackageRef: ContentRef;
  acceptanceManifestRef: ContentRef;
  hostCapabilityManifestRef: ContentRef;
  datasourceContractRefs: readonly ContentRef[];
  authContractRefs: readonly ContentRef[];
  approvedFixtureRefs: readonly ContentRef[];
  publicationPolicyRef: ContentRef;
  requiredInventoryRows: readonly { kind: AcceptanceInventoryKind; id: string }[];
}

export interface ApplicationProfile {
  schemaVersion: "cogo.application-profile.v1";
  body: ApplicationProfileBody;
  bodyHash: ContentHash;
}

const HASH = /^sha256:[a-f0-9]{64}$/;
const NON_EMPTY = /\S/;
const INVENTORY_KINDS = new Set<AcceptanceInventoryKind>([
  "role", "route", "surface", "journey", "branch", "event", "permission", "policy",
  "action", "state", "datasource", "error-path", "recovery-path", "terminal-outcome",
]);

function compare(left: string, right: string): number { return left < right ? -1 : left > right ? 1 : 0; }
function assertPlainObject(value: unknown, label: string): asserts value is Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value) || Object.getPrototypeOf(value) !== Object.prototype) throw new Error(`APPLICATION_PROFILE_INVALID_${label}`);
}
function assertExactKeys(value: unknown, keys: readonly string[], label: string): asserts value is Record<string, unknown> {
  assertPlainObject(value, label);
  const actual = Object.keys(value);
  if (actual.length !== keys.length || actual.some((key) => !keys.includes(key)) || keys.some((key) => !Object.prototype.hasOwnProperty.call(value, key))) throw new Error(`APPLICATION_PROFILE_INVALID_${label}`);
}
function assertText(value: unknown, label: string): asserts value is string {
  if (typeof value !== "string" || !NON_EMPTY.test(value)) throw new Error(`APPLICATION_PROFILE_INVALID_${label}`);
}
function assertContentRef(value: unknown, label: string): asserts value is ContentRef {
  assertExactKeys(value, ["ref", "contentHash"], label);
  assertText(value.ref, `${label}_REF`);
  if (typeof value.contentHash !== "string" || !HASH.test(value.contentHash)) throw new Error(`APPLICATION_PROFILE_INVALID_${label}`);
}
function assertRefArray(value: unknown, label: string): asserts value is readonly ContentRef[] {
  if (!Array.isArray(value) || Object.getPrototypeOf(value) !== Array.prototype) throw new Error("APPLICATION_PROFILE_INVALID_ARRAY");
  for (const ref of value) {
    assertContentRef(ref, label);
  }
}
function sortedRefs(refs: readonly ContentRef[]): ContentRef[] {
  return refs.map((ref) => ({ ref: ref.ref, contentHash: ref.contentHash })).sort((left, right) => compare(left.ref, right.ref) || compare(left.contentHash, right.contentHash));
}
function assertBody(value: unknown): asserts value is ApplicationProfileBody {
  assertExactKeys(value, ["profileId", "profileVersion", "applicationId", "displayName", "applicationOntologyPackageRef", "acceptanceManifestRef", "hostCapabilityManifestRef", "datasourceContractRefs", "authContractRefs", "approvedFixtureRefs", "publicationPolicyRef", "requiredInventoryRows"], "BODY");
  for (const field of ["profileId", "profileVersion", "applicationId", "displayName"] as const) assertText(value[field], field.toUpperCase());
  if (typeof value.displayName !== "string" || value.displayName.startsWith("#")) throw new Error("APPLICATION_PROFILE_INVALID_DISPLAY_NAME");
  for (const field of ["applicationOntologyPackageRef", "acceptanceManifestRef", "hostCapabilityManifestRef", "publicationPolicyRef"] as const) assertContentRef(value[field], field.toUpperCase());
  assertRefArray(value.datasourceContractRefs, "DATASOURCE_CONTRACT_REFS");
  assertRefArray(value.authContractRefs, "AUTH_CONTRACT_REFS");
  assertRefArray(value.approvedFixtureRefs, "APPROVED_FIXTURE_REFS");
  const allRefs: Array<{ owner: string; ref: ContentRef }> = [
    { owner: "applicationOntologyPackageRef", ref: value.applicationOntologyPackageRef as ContentRef },
    { owner: "acceptanceManifestRef", ref: value.acceptanceManifestRef as ContentRef },
    { owner: "hostCapabilityManifestRef", ref: value.hostCapabilityManifestRef as ContentRef },
    ...(value.datasourceContractRefs as readonly ContentRef[]).map((ref, index) => ({ owner: `datasourceContractRefs[${index}]`, ref })),
    ...(value.authContractRefs as readonly ContentRef[]).map((ref, index) => ({ owner: `authContractRefs[${index}]`, ref })),
    ...(value.approvedFixtureRefs as readonly ContentRef[]).map((ref, index) => ({ owner: `approvedFixtureRefs[${index}]`, ref })),
    { owner: "publicationPolicyRef", ref: value.publicationPolicyRef as ContentRef },
  ];
  const refsByRole = new Map<string, { contentHash: string; owner: string }>();
  for (const { owner, ref } of allRefs) {
    const previous = refsByRole.get(ref.ref);
    if (previous) {
      if (previous.contentHash !== ref.contentHash) throw new Error(`APPLICATION_PROFILE_CONTENT_REF_HASH_CONFLICT:${ref.ref}`);
      throw new Error(`APPLICATION_PROFILE_CONTENT_REF_REUSED:${ref.ref}`);
    }
    refsByRole.set(ref.ref, { contentHash: ref.contentHash, owner });
  }
  if (!Array.isArray(value.requiredInventoryRows) || Object.getPrototypeOf(value.requiredInventoryRows) !== Array.prototype) throw new Error("APPLICATION_PROFILE_INVALID_REQUIRED_INVENTORY_ROWS");
  const rows = new Set<string>();
  for (const row of value.requiredInventoryRows) {
    assertExactKeys(row, ["kind", "id"], "REQUIRED_INVENTORY_ROW");
    if (!INVENTORY_KINDS.has(row.kind as AcceptanceInventoryKind)) throw new Error("APPLICATION_PROFILE_INVALID_REQUIRED_INVENTORY_ROW");
    assertText(row.id, "REQUIRED_INVENTORY_ROW_ID");
    const identity = `${row.kind}:${row.id}`;
    if (rows.has(identity)) throw new Error(`APPLICATION_PROFILE_DUPLICATE_REQUIRED_INVENTORY_ROW:${identity}`);
    rows.add(identity);
  }
}
function canonicalBody(body: ApplicationProfileBody): ApplicationProfileBody {
  return {
    ...body,
    applicationOntologyPackageRef: { ...body.applicationOntologyPackageRef },
    acceptanceManifestRef: { ...body.acceptanceManifestRef },
    hostCapabilityManifestRef: { ...body.hostCapabilityManifestRef },
    datasourceContractRefs: sortedRefs(body.datasourceContractRefs),
    authContractRefs: sortedRefs(body.authContractRefs),
    approvedFixtureRefs: sortedRefs(body.approvedFixtureRefs),
    publicationPolicyRef: { ...body.publicationPolicyRef },
    requiredInventoryRows: body.requiredInventoryRows.map((row) => ({ ...row })).sort((left, right) => compare(left.kind, right.kind) || compare(left.id, right.id)),
  };
}
export function hashApplicationProfileBody(body: ApplicationProfileBody): ContentHash { return hashCanonicalContent(canonicalBody(body)); }
export function assertApplicationProfileIntrinsicIntegrity(value: ApplicationProfile): void {
  assertExactKeys(value, ["schemaVersion", "body", "bodyHash"], "ENVELOPE");
  if (value.schemaVersion !== "cogo.application-profile.v1") throw new Error("APPLICATION_PROFILE_SCHEMA_VERSION_MISMATCH");
  if (typeof value.bodyHash !== "string" || !HASH.test(value.bodyHash)) throw new Error("APPLICATION_PROFILE_INVALID_BODY_HASH");
  assertBody(value.body);
  if (hashApplicationProfileBody(value.body) !== value.bodyHash) throw new Error("APPLICATION_PROFILE_HASH_MISMATCH");
}
export function applicationProfileContentRef(value: ApplicationProfile): ContentRef {
  assertApplicationProfileIntrinsicIntegrity(value);
  return { ref: `cogo-content:application-profile:${value.bodyHash}`, contentHash: value.bodyHash };
}
